import {
  getProtocolBrand,
  readLastBrand,
  rememberLastBrand,
} from './brandCache';
import { brandForPath } from './brandForPath';
import { preloadBrandLogo } from './preloadBrandLogo';
import { protocolIdFromPath } from './protocolIdFromPath';

const DEFAULT_TIMEOUT_MS = 1500;

const fetchWithinTimeout = async (
  protocolId: string,
  timeoutMs: number
): Promise<string | undefined> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<'timeout'>(resolve => {
    timer = setTimeout(() => {
      resolve('timeout');
    }, timeoutMs);
  });
  try {
    const brand = await Promise.race([getProtocolBrand(protocolId), timeout]);
    if (brand === 'timeout') {
      if (readLastBrand()?.protocolId !== protocolId)
        rememberLastBrand(protocolId, null);
      return undefined;
    }
    rememberLastBrand(protocolId, brand?.pointer ?? null);
    return brand?.pointer.id;
  } finally {
    clearTimeout(timer);
  }
};

// Starts the brand request for the protocol of the page the app loads on, so
// a login redirect finds it under way, and the request for the logo of the
// page's brand: the protocol's, which a cached brand gives at once; off a
// protocol path, the cached last brand's on the no-session routes.
export const startLoginBrand = (pathname: string) => {
  const protocolId = protocolIdFromPath(pathname);
  if (protocolId === undefined) {
    preloadBrandLogo(brandForPath(pathname));
    return;
  }
  void getProtocolBrand(protocolId).then(brand => {
    preloadBrandLogo(brand?.file);
  });
};

// The brand id for the login of the page at `pathname`, waiting at most
// `timeoutMs` from the call; Identity can use it to show the brand.
export const resolveLoginBrandId = async (
  pathname: string,
  timeoutMs = DEFAULT_TIMEOUT_MS
): Promise<string | undefined> => {
  const protocolId = protocolIdFromPath(pathname);
  return protocolId === undefined
    ? undefined
    : fetchWithinTimeout(protocolId, timeoutMs);
};
